/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ChartLegendEl} from 'common/components/chart-legend';
import {BarVizEl} from 'common/charts/visualization/bar-viz';
import {LineChartEl} from 'common/charts/visualization/line-chart';
import {MultiMetricChartEl} from 'common/charts/visualization/multi-metric';
import {PieChartEl} from 'common/charts/visualization/pie-chart';
import COLORS from 'common/stylesheets/mixins/colors.json';
import {BucketPaneEl} from 'common/widgets/bucket-pane';
import {BucketSelectorEl} from 'common/widgets/bucket-selector';
import {SankeyChartEl} from 'common/widgets/sankey-chart';
import {ChartHeaderEl} from 'common/widgets/chart-header';

import {CodeTipEl} from '../components/code-tip';
import type {DesignSystem} from '../index';
import {Section, SubSection} from './utils';

export function ChartsSection({designSystem}: {designSystem: DesignSystem}) {
  return (
    <Section title='Charts'>
      <SubSection title='mp-chart-header' autoDoc={true}>
        <CodeTipEl sel='.example.block'>
          <ChartHeaderEl
            attrs={{
              'chart-types': Object.values(designSystem.helpers.ChartType),
              'chart-type-overrides': {
                [designSystem.helpers.ChartType.Pie]: {
                  isDisabled: true,
                  tooltip: {
                    text: `No pie`,
                  },
                },
              },
              'date-units': JSON.stringify(Object.values(designSystem.helpers.TimeUnit)),
              'value-representations': JSON.stringify(Object.values(designSystem.helpers.ValueRepresentationType)),
              'selected-chart-type': designSystem.helpers.ChartType.FunnelSteps,
              'selected-date-unit': designSystem.helpers.TimeUnit.Hour,
              'selected-value-representation': designSystem.helpers.ValueRepresentationType.Relative,
              'show-date-range-selector': true,
              'project-id': 3,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-pie-chart' autoDoc={true}>
        <p sel='.desc'>A basic pie chart. Pie chart data is passed in via `chartData` prop.</p>

        <CodeTipEl sel='.example' attrs={{'target-component': `mp-pie-chart`}}>
          <PieChartEl
            style={{height: `250px`, width: `250px`}}
            props={{
              chartData: [
                {
                  name: `funnels query`,
                  value: 434861,
                },
                {
                  name: `insights query`,
                  value: 424843,
                },
                {
                  name: `segmentation query`,
                  value: 325916,
                },
                {
                  name: `$opt_in`,
                  value: 287461,
                },
                {
                  name: `other`,
                  value: 202736,
                },
                {
                  name: `updated selected dashboard`,
                  value: 92863,
                },
              ],
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-line-chart' autoDoc={true}>
        <p sel='.desc'>Basic line charts, data is passed in via `chartData` prop.</p>

        <p sel='.desc'>Time data</p>
        <CodeTipEl sel='.example' attrs={{'target-component': `mp-line-chart`}}>
          <LineChartEl
            props={{
              chartData: designSystem.helpers.getLineChartData(true),
            }}
            attrs={{
              'chart-margin-left': 10,
              'display-analysis-type': `linear`,
              'hide-y-axis-ticks': true,
              'x-axis-options': designSystem.helpers.getXAxisOptionsTimeData(),
              'time-unit': `day`,
              'utc-offset': `-420`,
            }}
          />
        </CodeTipEl>

        <p sel='.desc'>Line data (not dates)</p>
        <CodeTipEl sel='.example' attrs={{'target-component': `mp-line-chart`}}>
          <LineChartEl
            props={{
              chartData: designSystem.helpers.getLineChartData(false),
              formatPointX: (x) => x.toLocaleString(),
            }}
            attrs={{
              'display-analysis-type': `linear`,
              'display-value-option': `absolute`,
              'x-axis-options': designSystem.helpers.getXAxisOptionsNumberData(),
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-sankey-chart' autoDoc={true}>
        <p sel='.desc'>Sankey visualization, data is passed in via `data` prop.</p>

        <CodeTipEl sel='.example' attrs={{'target-component': `mp-sankey-chart`}}>
          <SankeyChartEl
            props={{
              data: designSystem.helpers.getSankeyChartData(),
            }}
            attrs={{
              alignbreakdowns: true,
              alwaysbreakdown: true,
              reverse: false,
              'can-decrease-cardinality': true,
              'disable-add-step': true,
              'disable-lift': true,
              'tooltip-enabled': true,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-bar-viz' autoDoc={true}>
        <p sel='.desc'>Bar visualization, data passed by chart prop</p>

        <CodeTipEl sel='.example' attrs={{'target-component': `mp-bar-viz`}}>
          <BarVizEl
            props={{
              chartData: designSystem.helpers.barVizData,
            }}
            attrs={{
              'enable-bar-click-events': true,
            }}
          />
        </CodeTipEl>

        <CodeTipEl sel='.example'>
          <BarVizEl
            props={{
              chartData: designSystem.helpers.stackedBarVizData,
            }}
            attrs={{
              'enable-bar-click-events': true,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-multi-metric' autoDoc={true}>
        <p sel='.desc'>Simple big number visualization</p>

        <CodeTipEl sel='.example' attrs={{'target-component': `mp-metric`}}>
          <div sel='.resizeable'>
            <MultiMetricChartEl
              attrs={{
                metrics: [
                  {
                    value: 123456,
                    metric: `Events`,
                    label: `Conversions`,
                    subLabel: `Total`,
                    comparisonValue: 123000,
                    color: COLORS[`$chart1`],
                  },
                ],
              }}
            />
          </div>
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-chart-legend' autoDoc={true}>
        <CodeTipEl sel='.example' attrs={{'target-component': `mp-chart-legend`}}>
          <ChartLegendEl
            attrs={{
              segments: designSystem.helpers.getLineChartData(true).map((series) => series.name),
              'enable-search': true,
              'show-select-all': true,
            }}
          />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-bucket-selector' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <BucketSelectorEl attrs={designSystem.helpers.getBucketPaneAttributes()} />
        </CodeTipEl>
      </SubSection>

      <SubSection title='mp-bucket-pane' autoDoc={true}>
        <CodeTipEl sel='.example'>
          <ButtonEl
            attrs={{theme: ButtonTheme.Secondary}}
            on={{
              click: () =>
                !designSystem.state.open.bucketPane
                  ? designSystem.helpers.openModal(`bucketPane`)
                  : designSystem.helpers.closeModal(`bucketPane`),
            }}
          >
            Show the bucket pane component
          </ButtonEl>
          {/* This is under a button to prevent an input focus always going here */}
          {designSystem.state.open.bucketPane && (
            <BucketPaneEl attrs={designSystem.helpers.getBucketPaneAttributes()} />
          )}
        </CodeTipEl>
      </SubSection>
    </Section>
  );
}
